import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';

import RapportTICFE from './RapportTICFE';
import RapportTICGN from './RapportTICGN';
import RapportTURPE from './RapportTURPE';
import RapportTURPEMulti from './RapportTURPEMulti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Page publique de consultation d'un rapport via un token de partage.
 * Aucune authentification requise.
 */
export default function RapportShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Lien invalide');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/share/${token}`);
        if (r.status === 404) {
          setError('Ce lien est introuvable ou a été révoqué.');
        } else if (r.status === 410) {
          const body = await r.json().catch(() => ({}));
          setError(body.detail || 'Ce lien a expiré ou a été révoqué.');
        } else if (!r.ok) {
          setError('Une erreur est survenue lors du chargement du rapport.');
        } else {
          setData(await r.json());
        }
      } catch (e) {
        setError('Impossible de charger le rapport.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="share-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#397DFF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="share-error">
        <div className="text-center max-w-md">
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Lien indisponible</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data || !data.rapport) return null;

  const commonProps = {
    readOnly: true,
    shareData: data.rapport,
    shareToken: token,
  };

  switch (data.rapport_type) {
    case 'ticfe':
      return <RapportTICFE {...commonProps} />;
    case 'ticgn':
      return <RapportTICGN {...commonProps} />;
    case 'turpe':
      return <RapportTURPE {...commonProps} />;
    case 'turpe_multi':
      return <RapportTURPEMulti {...commonProps} />;
    default:
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-gray-800">Type de rapport non supporté</h1>
          </div>
        </div>
      );
  }
}
